/**
 * SendHoldBanner — the deployment-wide send hold (spec §11.8), read off
 * GET /outreach/status.
 *
 * The hold is a restore-safety guard, not a campaign setting: a restored
 * database could otherwise re-send everything its queue still holds. Nothing
 * on this screen can lift it — only an operator can — so the banner states the
 * fact and stops there.
 *
 * Renders nothing at all unless the hold is on, including while the status is
 * still loading: a banner that flashes on every tab open would train staff to
 * ignore it.
 */
import { useCan } from '@discovr/common';
import { useOutreachStatus } from '@discovr/features';
import { SCOPES } from '@discovr/types';
import { Warning } from '../../../design-system/icons';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert';

export function SendHoldBanner() {
  const canRead = useCan(SCOPES.outreachRead);
  const { data } = useOutreachStatus(canRead);

  if (!data?.sendHold) return null;

  return (
    // role="status" (polite), not the Alert default role="alert": a standing
    // condition, not an interruption.
    <Alert role="status" className="border-amber-200 bg-amber-50 text-[var(--foreground-warning-default)]">
      <Warning size={16} />
      <AlertTitle>Sending is on hold</AlertTitle>
      <AlertDescription className="text-[var(--foreground-warning-default)]/90">
        Queued first touches will go out once an operator lifts the hold.
      </AlertDescription>
    </Alert>
  );
}
